import * as React from "react";
import { Button, FormControl, FormLabel, Input, Stack, Textarea, Typography } from "@mui/joy";

import { getISTDateString } from "../../utils/date";

export interface QuoteFormValues {
  text: string;
  author?: string;
  date?: string;
}

interface AdminQuoteFormProps {
  initial?: QuoteFormValues;
  onSubmit: (data: QuoteFormValues) => Promise<void> | void;
  submitLabel?: string;
  loading?: boolean;
}

export default function AdminQuoteForm({ initial, onSubmit, submitLabel = "Save quote", loading = false }: AdminQuoteFormProps) {
  const [form, setForm] = React.useState({ text: "", author: "", date: getISTDateString() });

  React.useEffect(() => {
    setForm({
      text: initial?.text ?? "",
      author: initial?.author ?? "",
      date: initial?.date ?? getISTDateString(),
    });
  }, [initial]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const payload: QuoteFormValues = {
      text: form.text.trim(),
      author: form.author.trim() ? form.author.trim() : undefined,
      date: form.date || undefined,
    };
    await onSubmit(payload);
  };

  return (
    <Stack component="form" onSubmit={handleSubmit} spacing={2.5}>
      <FormControl required>
        <FormLabel sx={{ fontWeight: 600 }}>Quote text</FormLabel>
        <Textarea
          name="text"
          value={form.text}
          onChange={handleChange}
          minRows={4}
          variant="soft"
          placeholder="“Those who chant sincerely are nourished by Sri Krishna’s mercy.”"
          required
          sx={{ borderRadius: "lg", boxShadow: "inset 0 1px 0 rgba(15,23,42,0.08)" }}
        />
        <Typography level="body-xs" sx={{ color: "text.tertiary", mt: 0.5 }}>
          Share the full reflection exactly as devotees will read it across the dashboard.
        </Typography>
      </FormControl>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <FormControl sx={{ flex: 1 }}>
          <FormLabel sx={{ fontWeight: 600 }}>Author</FormLabel>
          <Input
            name="author"
            value={form.author}
            onChange={handleChange}
            placeholder="Srila Prabhupada"
            sx={{ borderRadius: "lg" }}
          />
          <Typography level="body-xs" sx={{ color: "text.tertiary", mt: 0.5 }}>
            Optional — highlight whose wisdom you are sharing.
          </Typography>
        </FormControl>
        <FormControl sx={{ flex: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
            <FormLabel sx={{ fontWeight: 600 }}>Date (IST)</FormLabel>
            <Button
              type="button"
              size="sm"
              variant="plain"
              color="neutral"
              onClick={() => setForm((prev) => ({ ...prev, date: getISTDateString() }))}
            >
              Use today
            </Button>
          </Stack>
          <Input
            name="date"
            type="date"
            value={form.date}
            onChange={handleChange}
            sx={{ borderRadius: "lg" }}
          />
          <Typography level="body-xs" sx={{ color: "text.tertiary", mt: 0.5 }}>
            Quotes are unique per calendar day in Asia/Kolkata. Adjust this date to reschedule, or leave it blank to use
            today.
          </Typography>
        </FormControl>
      </Stack>

      <Stack direction="row" justifyContent="flex-end">
        <Button type="submit" size="md" variant="solid" color="primary" loading={loading}>
          {submitLabel}
        </Button>
      </Stack>
    </Stack>
  );
}
